/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ActionGroup } from 'client-code/actions';
import { ScreenType } from 'client-code/previews';

import { APPLICATION_FEATURE_AREA } from '../../../config';
import { Channel } from '../../entities/Channel';
import { LiveTvError } from '../../error';
import { ServicesClientImpl } from '../../interactors';
import { IServices } from '../../interfaces/IServices';
import { Log, VuLogger } from '../../logging';
import { TActionList, TActionsParams, TOfferPreview } from '../../types';
import { TAiringOffer } from '../../types/TAiringOffer';
import { UniqueIdentifier } from '../../utils';
import { MetadataType } from '../enums';
import { IMetadata, IMetadataModel } from '../interfaces';

/* eslint-disable class-methods-use-this */
export class LinearMetadataModelImpl implements IMetadataModel {
    private static TAG: string = 'LinearMetadataModelImpl';

    private mMetadata: IMetadata = {};

    private mError: any = null;

    private mAiringCallId: number = 0;

    private mPreviewCallId: number = 0;

    private mServices: IServices = new ServicesClientImpl();

    private getAiringData(channel: Channel, playerCurrentTime?: number): Promise<void> {
        this.mAiringCallId += 1;
        return new Promise((resolve, reject) => {
            const { stationId } = channel;
            if (!stationId) {
                return reject();
            }
            this.mServices
                .fetchAiringOffers(stationId, this.mAiringCallId, playerCurrentTime)
                .then((response: TAiringOffer) => {
                    Log.i(LinearMetadataModelImpl.TAG, `fetchAiringData`);
                    // Condition to set only the response for last network call to avoid
                    //  mismatch of data
                    if (response.networkCallId === this.mAiringCallId) {
                        this.mMetadata.airingOffers = response.airingOffers;
                        resolve();
                        // TODO: Need to remove this once loging is done
                        // QoEAppLogger.updateContentPayload(channel, response.airingOffers);
                        // QoEAppLogger.logWatchContentStart();
                        // if (isFromNetwork) {
                        //     QoEAppLogger.logStreamingStartEvent();
                        //     QoEAppLogger.startMediaHealthTimer();
                        // }
                        VuLogger.updateLiveTvPayload(channel, response.airingOffers);
                        VuLogger.logWatchStartedEvent();
                    }
                })
                .catch((error) => {
                    Diagnostics.error(LinearMetadataModelImpl.TAG, `error in fetchAiringData`);
                    Diagnostics.error(LinearMetadataModelImpl.TAG, 'stacktrace:', error);
                    this.mError = error;
                    this.mMetadata.airingOffers = [];
                    this.mMetadata.offerPreview = {};
                    reject();
                });
        });
    }

    private getPreviewData(): Promise<void> {
        this.mPreviewCallId += 1;
        const offerId = this.mMetadata.airingOffers ? this.mMetadata.airingOffers[0].offerId : '';
        return new Promise((resolve, reject) => {
            this.mServices
                .fetchOfferPreviewV2(offerId, ScreenType.StandardBanner, this.mPreviewCallId)
                .then((response: TOfferPreview) => {
                    // Condition to set only the response for last network call to avoid mismatch
                    // of data
                    if (response.networkCallId === this.mPreviewCallId) {
                        this.mMetadata.offerPreview = response.offerPreview;
                        resolve();
                    }
                    Log.i(LinearMetadataModelImpl.TAG, 'fetchPreviewData');
                })
                .catch((error) => {
                    Diagnostics.error(LinearMetadataModelImpl.TAG, `error in fetchPreviewData`);
                    Diagnostics.error(LinearMetadataModelImpl.TAG, 'stacktrace:', error);
                    this.mError = error;
                    this.mMetadata.offerPreview = {};
                    reject();
                });
        });
    }

    private async retrieveActionList(channel): Promise<void> {
        const networkCallId = UniqueIdentifier.getNextActionsNetworkCallIdForLiveTv();
        const requestParams: TActionsParams = {
            offerId: this.mMetadata.airingOffers ? this.mMetadata.airingOffers[0].offerId : '',
            channelNumber: channel.channelNumber || '',
            groups: [
                ActionGroup.Watch,
                ActionGroup.ScheduleBookmark,
                ActionGroup.ScheduleOnepass,
                ActionGroup.Rent,
                ActionGroup.ScheduleRecording,
            ],
            screenType: APPLICATION_FEATURE_AREA.PLAYER,
            actionsNetworkCallId: networkCallId,
        };
        return new Promise((resolve, reject) => {
            this.mServices
                .getOfferActionsV2(requestParams)
                .then((response: TActionList) => {
                    if (
                        response.actionsNetworkCallId ===
                        UniqueIdentifier.getActionsNetworkCallIdForLiveTv()
                    ) {
                        this.mMetadata.actionList = response.actionList;
                        resolve();
                    }
                })
                .catch((error) => {
                    this.mError = error;
                    reject(error);
                });
        });
    }

    public getError(): LiveTvError {
        return this.mError;
    }

    public getMetadata(): IMetadata {
        return this.mMetadata;
    }

    public prepareMetadataByType(playbackType: {
        metadataType: MetadataType.AIRING | MetadataType.PREVIEW | MetadataType.ACTION_LIST;
        channel: Channel;
        playerCurrentTime?: number;
    }): Promise<void> {
        const { metadataType, channel, playerCurrentTime } = playbackType;
        switch (metadataType) {
            case MetadataType.AIRING:
                return this.getAiringData(channel, playerCurrentTime);
            case MetadataType.ACTION_LIST:
                return this.retrieveActionList(channel);
            default:
                return this.getPreviewData();
        }
    }
}
